import { useCallback, useEffect, useMemo, useState } from 'react'

import { ActionIconEnum } from 'common/enums/ActionIcon'
import { TagEventsEnum } from 'common/enums/TagEventsEnum'
import { BannerCapTorcedor, FlexRow, ListDescription, Separator } from 'components'
import { CustomAccordion } from 'components/CustomAccordion/CustomAccordion'
import BaseBridge from 'config/bridge/BaseBridge'
import { useNavBar } from 'hooks/useNavBar'
import { useHistory } from 'react-router-dom'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import useBoundState from 'store'
import { ITicketDetails } from 'store/ticketDetails/types'
import { convertCurrentDateTimeToString } from 'utils/commons'
import { Icons } from 'utils/icons'
import { RedemptionStatusEnum } from 'common/enums/RedemptionStatusEnum'
import { TabIdEnum } from 'common/enums/TabIdEnum'
import bannerCapTorcedor from 'assets/png/banner-cap-torcedor.png'

import { Alert } from '@interco/inter-ui/components/Alert'
import { Flex } from '@interco/inter-ui/components/Flex'
import { Tag } from '@interco/inter-ui/components/Tag'
import { Text } from '@interco/inter-ui/components/Text'
import { formatToBRL } from '@interco/lib-util'

import { RafflesCarousel } from '../../../components/RafflesCarousel/RafflesCarousel'
import * as S from './AfterSalesDetails.styles'
import {
  analyticsDetailsLoad,
  analyticsRepurchaseRedirectTag,
  AnalyticsTicketDetailsAccordion,
  AnalyticsTicketDetailsActionButtons,
  AnalyticsTicketDetailsHeader,
} from './AfterSalesDetails.tags'

enum ActionEnum {
  GENERAL_CONDITIONS,
  SUBSCRIPTION_FORM,
  CERTIFICATE,
}

const STATUS_LABELS: Record<string, string> = {
  AVAILABLE: 'Disponível',
  ACTIVE: 'Ativo',
  COMPLETED: 'Finalizado',
}

export const AfterSalesDetails = () => {
  const history = useHistory()

  const contentCapitalization = useBoundState((state) => state.contentCapitalization)
  const contentTitle = useBoundState((state) => state.contentTitle)

  const isLoading = useBoundState((state) => state.isLoading)
  const ticketInfo = useBoundState((state) => state.currentTicketDetails)
  const getUrlTitle = useBoundState((state) => state.getUrlTitle)
  const setSelectedTab = useBoundState((state) => state.setSelectedTab)

  const [expanded, setExpanded] = useState(false)
  const chevronRight = <Icons.ChevronRight color="var(--primary500)" width={24} height={24} />

  const handleBack = useCallback(() => {
    setTimeout(() => history.push(TypesRoutes.AFTER_SALES), 50)
  }, [history])

  useNavBar({
    actionIcons: {
      leftIcon: ActionIconEnum.BACK,
      rightIcon: ActionIconEnum.FAQ,
    },
    backListener: handleBack,
    backButtonCallback: () => AnalyticsTicketDetailsHeader('32', 'back'),
    helpButtonCallback: () => AnalyticsTicketDetailsHeader('33', 'ajuda'),
  })

  useEffect(() => {
    BaseBridge.tacWb({
      name: 'after_sales_details_screen_loaded',
      module: 'capitalizacao',
      actions: ['enter_after_sales_details'],
      params: {},
    })

    BaseBridge.requestAnalytics(TagEventsEnum.CAP_FRONT, {
      screen: 'raspadinha detalhes',
      cashback: JSON.stringify(ticketInfo.cashback),
      instantPrize: JSON.stringify(ticketInfo.instantPrize),
    })

    analyticsDetailsLoad({
      display_redemption_available_alert:
        ticketInfo.redemptionStatus === RedemptionStatusEnum.READY_TO_RELEASE ? 'true' : 'false',
    })
  }, [])

  const actionHandlers = useMemo(() => {
    const handlers: Record<ActionEnum, () => void> = {
      [ActionEnum.GENERAL_CONDITIONS]: () => {
        AnalyticsTicketDetailsActionButtons('39', 'Condições gerais')
        BaseBridge.openPdf(
          `https://static.bancointer.com.br/seguros/capitalization/condicoes-gerais/${ticketInfo.susepProcess}/CondicoesGerais_Sortezinha.pdf`,
          'Condições Gerais',
        )
      },
      [ActionEnum.SUBSCRIPTION_FORM]: () => {
        AnalyticsTicketDetailsActionButtons('40', 'Ficha de cadastro')
        history.push(TypesRoutes.REGISTRATION_FORM)
      },
      [ActionEnum.CERTIFICATE]: () => {
        AnalyticsTicketDetailsActionButtons('41', 'Titulo')
        getUrlTitle(ticketInfo.ticketId)
      },
    }

    return handlers
  }, [ticketInfo.susepProcess, ticketInfo.ticketId, history, getUrlTitle])

  const renderListItem = (title: string, actionEnum: ActionEnum) => (
    <ListDescription
      borderBottom
      margin="0 0 16px 0"
      iconRight={chevronRight}
      onClick={actionHandlers[actionEnum]}
      isLoading={isLoading}
    >
      <Flex direction="column">
        <Text variant="body-2" colorWeight={isLoading ? 300 : 500} bold>
          {title}
        </Text>
      </Flex>
    </ListDescription>
  )

  const redemptionAlertConfig = useMemo(() => {
    type AlertType = 'grayscale' | 'warning' | 'success'

    interface IRedemptionAlertConfig {
      type: AlertType
      content: string | React.JSX.Element
    }

    const config: Record<RedemptionStatusEnum, IRedemptionAlertConfig> = {
      [RedemptionStatusEnum.READY_TO_RELEASE]: {
        type: 'grayscale',
        content: (
          <>
            Você já pode resgatar seu {contentTitle.value1Lowercase}!
            <br />
            <Text
              data-testid="redemption-text-link"
              variant="caption-1"
              color="primary"
              bold
              colorWeight={500}
              onClick={() => {
                analyticsRepurchaseRedirectTag()
                setSelectedTab(TabIdEnum.REDEMPTION)
                history.push(`${TypesRoutes.AFTER_SALES}`)
              }}
              style={{ textDecoration: 'underline' }}
            >
              Resgatar
            </Text>
          </>
        ),
      },
      [RedemptionStatusEnum.SENT_TO_REDEMPTION]: {
        type: 'warning',
        content: 'Seu resgate está em processamento e em breve estará disponível.',
      },
      [RedemptionStatusEnum.REDEEMED]: {
        type: 'success',
        content: `Seu valor de ${formatToBRL(ticketInfo.redemptionAmount ?? 0)} foi resgatado em 
            ${convertCurrentDateTimeToString(ticketInfo.redemptionDate ?? '')}.`,
      },
    }

    return config
  }, [ticketInfo.redemptionAmount, ticketInfo.redemptionDate])

  const renderRedemptionAlert = () => {
    if (ticketInfo.redemptionStatus) {
      const alertConfig = redemptionAlertConfig[ticketInfo.redemptionStatus]
      return alertConfig ? <Alert type={alertConfig.type}>{alertConfig.content}</Alert> : null
    }

    return <Alert type="warning">Seu resgate de 50% ficará disponível no fim da vigência.</Alert>
  }

  const renderFlexRows = () => {
    const status = STATUS_LABELS[ticketInfo.status]

    const instantResult = ticketInfo.ticketAwarded ? (
      <S.StatusTag>
        <Tag theme="green">Você ganhou</Tag>
      </S.StatusTag>
    ) : (
      'Não foi dessa vez'
    )

    return (
      <>
        <FlexRow title="Status" value={status} />
        <FlexRow title="Número da sorte" value={ticketInfo.luckyNumber} />
        <FlexRow title="Resultado instantâneo" value={instantResult} />

        {ticketInfo.ticketAwarded && ticketInfo.instantPrize && (
          <>
            {ticketInfo.instantPrize.paymentDate && (
              <FlexRow
                title="Descrição do prêmio instantâneo"
                value={convertCurrentDateTimeToString(ticketInfo.instantPrize.paymentDate)}
              />
            )}

            {ticketInfo.instantPrize.prizeAmount && (
              <FlexRow
                title="Valor do prêmio instantâneo"
                value={formatToBRL(ticketInfo.instantPrize.prizeAmount)}
              />
            )}
          </>
        )}

        <Separator borderWidth="1px" type="dashed" />
        <FlexRow
          title={`Número do ${contentTitle.value1Lowercase}`}
          value={ticketInfo.titleNumber}
        />
        <FlexRow
          title="Data da compra"
          value={convertCurrentDateTimeToString(ticketInfo.purchaseDate)}
        />
        <FlexRow
          title="Fim da vigência"
          value={convertCurrentDateTimeToString(ticketInfo.validityEnd)}
        />
        <FlexRow title="Valor da compra" value={formatToBRL(ticketInfo.purchaseValue)} />

        {ticketInfo.warningMessage && (
          <S.AlertWrapper>
            <Alert type="warning">{ticketInfo.warningMessage}</Alert>
          </S.AlertWrapper>
        )}
      </>
    )
  }

  const toggleCustomAccordion = (info: ITicketDetails) => {
    const result = info.ticketAwarded ? 'Você ganhou' : 'Não foi dessa vez'
    AnalyticsTicketDetailsAccordion(info.status, result)
    setExpanded(!expanded)
  }

  return (
    <S.Container>
      <S.AccordionSection data-testid="accordion-section">
        <CustomAccordion
          labelComponent={
            <S.Header data-testid="test-accordion" $expanded={expanded}>
              <Text variant="body-2" colorWeight={500} bold>
                {contentCapitalization.value1}
              </Text>
              <Icons.ChevronDown width={24} height={24} color="var(--primary500)" />
            </S.Header>
          }
          expanded={expanded}
          maxHeightWhenClosed={ticketInfo.ticketAwarded ? '124px' : '80px'}
          onClick={() => toggleCustomAccordion(ticketInfo)}
        >
          <Flex direction="column" gap="16px">
            {renderFlexRows()}
          </Flex>
        </CustomAccordion>
      </S.AccordionSection>

      <S.Section>
        <S.RedemptionAlertWrapper>{renderRedemptionAlert()}</S.RedemptionAlertWrapper>
      </S.Section>

      <S.Section data-testid="raffles-section">
        <Text variant="body-2" colorWeight={500} bold>
          Sorteios
        </Text>
        {ticketInfo.raffles && (
          <RafflesCarousel luckyNumber={ticketInfo.luckyNumber} raffles={ticketInfo.raffles} />
        )}
      </S.Section>

      <Separator color="var(--gray100)" margin="32px 0" borderWidth="8px" />

      <S.Section>
        {renderListItem('Condições Gerais', ActionEnum.GENERAL_CONDITIONS)}
        {renderListItem('Ficha de Cadastro', ActionEnum.SUBSCRIPTION_FORM)}
        {renderListItem('Título', ActionEnum.CERTIFICATE)}
      </S.Section>

      <S.Section>
        <BannerCapTorcedor img={bannerCapTorcedor} screen="sortezinha detalhes" />
      </S.Section>
    </S.Container>
  )
}
